<template>
  <section>
    <header class="navbar" :class="{ hidden: hidden }">
      <!-- Company Logo -->
      <router-link to="/" class="logo" aria-label="IpromiseAfrica home" @click="closeMobileNav">
        <img
          :src="currentLogo"
          alt="IPA Logo"
          fetchpriority="high"
          loading="eager"
          decoding="async"
        />
      </router-link>

      <!-- Center Navigation Items -->
      <nav id="main-nav" :class="{ open: menuOpen }" aria-label="Main navigation">
        <router-link to="/ipromiseafrica/store" class="nav-item" @click="closeMobileNav">IPA Store</router-link>
        <router-link to="/ipromiseafrica/kids" class="nav-item" @click="closeMobileNav">Kids Store</router-link>
        <router-link to="/ipromiseafrica/resale" class="nav-item" @click="closeMobileNav">Resale</router-link>
        <router-link to="/ipromiseafrica/facility" class="nav-item" @click="closeMobileNav">Book Facility</router-link>
        <router-link to="/ipromiseafrica/support" class="nav-item" @click="closeMobileNav">Support</router-link>
        <router-link to="/ipromiseafrica/matchup/league" class="nav-item" @click="closeMobileNav">IPA League</router-link>
        <router-link to="/ipromiseafrica/tournament" class="nav-item" @click="closeMobileNav">Tournaments</router-link>
        <router-link
          :to="{ name: pathfinderRouteName }"
          class="nav-item"
          @click="closeMobileNav"
        >
          Pathfinder
        </router-link>
        <router-link
          :to="{ name: theAbleRouteName }"
          class="nav-item"
          @click="closeMobileNav"
        >
          The Able
        </router-link>
        <router-link to="/ipromiseafrica/matchup" class="nav-item" @click="closeMobileNav">Match Hub</router-link>
      </nav>

      <!-- Icons -->
      <div class="icons">
        <button
          type="button"
          class="search-trigger"
          aria-label="Search pages (Ctrl+K)"
          title="Search pages (Ctrl+K)"
          @click="openRouteSearch"
        >
          <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
        </button>

        <!-- User Menu Dropdown -->
        <div
          class="user-menu-wrapper"
          @click.stop="toggleUserMenu"
          v-click-outside="onUserMenuClickOutside"
        >
          <button
            type="button"
            class="user-menu-trigger"
            :aria-expanded="userMenuOpen"
            aria-haspopup="true"
            aria-label="Account menu"
          >
            <i class="fa-solid fa-user-circle"></i>
            <i class="fa-solid fa-chevron-down dropdown-arrow" :class="{ rotated: userMenuOpen }"></i>
          </button>
          
          <!-- Dropdown Menu -->
          <transition name="dropdown-fade">
            <div v-if="userMenuOpen" class="user-menu-dropdown" :class="{ 'mobile-dropdown': isMobile }">
              <!-- Desktop: Account row + flyout submenu -->
              <div
                v-if="!isMobile"
                class="menu-item has-submenu"
                @mouseenter="showAccountSubmenu = true"
                @mouseleave="showAccountSubmenu = false"
                @click.stop="showAccountSubmenu = !showAccountSubmenu"
              >
                <div class="menu-item-header">
                  <i class="fa-solid fa-user"></i>
                  <span>Account</span>
                  <i class="fa-solid fa-chevron-left submenu-arrow" :class="{ rotated: showAccountSubmenu }"></i>
                </div>
                <transition name="submenu-fade">
                  <div
                    v-if="showAccountSubmenu"
                    class="submenu"
                    @mouseenter="showAccountSubmenu = true"
                    @mouseleave="showAccountSubmenu = false"
                  >
                    <template v-if="!authStatus">
                      <router-link to="/ipromiseafrica/sign-in" class="submenu-item" @click="closeUserMenu">
                        <i class="fa-solid fa-sign-in-alt"></i>
                        <span>Sign In</span>
                      </router-link>
                      <router-link to="/ipromiseafrica/account" class="submenu-item" @click="closeUserMenu">
                        <i class="fa-solid fa-user-plus"></i>
                        <span>Sign Up</span>
                      </router-link>
                    </template>
                    <button v-else type="button" class="submenu-item logout-btn" @click="handleLogout">
                      <i class="fa-solid fa-sign-out-alt"></i>
                      <span>Logout</span>
                    </button>
                  </div>
                </transition>
              </div>

              <!-- Mobile (≤768px): flat Sign In / Sign Up / Logout — no Account submenu -->
              <template v-if="isMobile">
                <template v-if="!authStatus">
                  <router-link to="/ipromiseafrica/sign-in" class="menu-item menu-item-link" @click="closeUserMenu">
                    <i class="fa-solid fa-sign-in-alt"></i>
                    <span>Sign In</span>
                  </router-link>
                  <router-link to="/ipromiseafrica/account" class="menu-item menu-item-link" @click="closeUserMenu">
                    <i class="fa-solid fa-user-plus"></i>
                    <span>Sign Up</span>
                  </router-link>
                </template>
                <button v-else type="button" class="menu-item logout-btn" @click="handleLogout">
                  <i class="fa-solid fa-sign-out-alt"></i>
                  <span>Logout</span>
                </button>
              </template>

              <!-- Wishlist -->
              <router-link to="/ipromiseafrica/store/wishlist" class="menu-item" @click="closeUserMenu">
                <i class="fa-solid fa-heart"></i>
                <span>Wishlist</span>
              </router-link>

              <!-- Marketplace saved products (GET /seller/saved-products; auth via route meta) -->
              <router-link
                to="/ipromiseafrica/resale/saved-listings"
                class="menu-item menu-item-link"
                @click="closeUserMenu"
              >
                <i class="fa-solid fa-bookmark"></i>
                <span>Saved products</span>
              </router-link>

              <!-- My Pitch -->
              <button class="menu-item" @click="handleMyPitchClick" v-if="authStatus">
                <i class="fa-solid fa-futbol"></i>
                <span>My Pitch</span>
              </button>

              <!-- My Team -->
              <button class="menu-item" @click="handleMyTeamClick" v-if="authStatus">
                <i class="fa-solid fa-users"></i>
                <span>My Team</span>
              </button>

              <!-- Player Dashboard -->
              <button class="menu-item" @click="handlePlayerDashboardClick" v-if="authStatus">
                <i class="fa-solid fa-user-tie"></i>
                <span>Player Dashboard</span>
              </button>

              <!-- Seller Dashboard -->
              <button class="menu-item" @click="handleSellerDashboardClick" v-if="authStatus">
                <i class="fa-solid fa-store"></i>
                <span>Seller Dashboard</span>
              </button>

              <!-- Team invites (received) — prefetch validates session before opening page -->
              <button
                class="menu-item"
                type="button"
                :disabled="teamInvitesNavLoading"
                @click="handleTeamInvitesClick"
                v-if="authStatus"
              >
                <i
                  class="fa-solid"
                  :class="teamInvitesNavLoading ? 'fa-spinner fa-spin' : 'fa-envelope-open-text'"
                ></i>
                <span>Team invites</span>
              </button>
            </div>
          </transition>
        </div>

        <!-- 🛒 Cart Icon with Count -->
        <div class="cart-icon" @click="goToCart">
          <i class="fa-solid fa-cart-flatbed"></i>
          <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
        </div>

        <!-- 🌙 Theme Toggle -->
        <button id="theme-toggle" @click="toggleTheme">
          <i v-if="theme === 'light'" class="fa-solid fa-moon"></i>
          <i v-else class="fa-solid fa-sun"></i>
        </button>

        <!-- ☰ Mobile nav (visible ≤768px) -->
        <button
          type="button"
          class="menu-btn"
          :aria-expanded="menuOpen"
          aria-controls="main-nav"
          :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
          @click.stop="toggleMenu"
        >
          <i class="fa-solid fa-bars" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <Teleport to="body">
      <Transition name="route-search-overlay">
        <div
          v-if="routeSearchOpen"
          class="route-search-backdrop"
          role="presentation"
          @click.self="closeRouteSearch"
        >
          <div
            class="route-search-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="route-search-heading"
            @keydown.stop
          >
            <div class="route-search-field">
              <i class="fa-solid fa-magnifying-glass route-search-field-icon" aria-hidden="true"></i>
              <input
                id="route-search-input"
                ref="routeSearchInput"
                v-model="routeSearchQuery"
                type="search"
                class="route-search-input"
                placeholder="Search customer pages…"
                autocomplete="off"
                autocorrect="off"
                spellcheck="false"
                aria-describedby="route-search-hint"
                @keydown.down.prevent="moveRouteSearchSelection(1)"
                @keydown.up.prevent="moveRouteSearchSelection(-1)"
                @keydown.enter.prevent="confirmRouteSearchSelection"
                @keydown.esc.prevent="closeRouteSearch"
              />
              <button type="button" class="route-search-close" aria-label="Close search" @click="closeRouteSearch">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>
            <p id="route-search-hint" class="route-search-meta">
              <span id="route-search-heading" class="sr-only">Search site pages</span>
              Customer routes only · <kbd class="route-search-kbd">↑</kbd><kbd class="route-search-kbd">↓</kbd> navigate ·
              <kbd class="route-search-kbd">Enter</kbd> open · <kbd class="route-search-kbd">Esc</kbd> close
            </p>
            <ul class="route-search-list" role="listbox" :aria-activedescendant="activeRouteSearchId">
              <li
                v-if="!routeSearchResults.length"
                class="route-search-empty"
                role="option"
                aria-disabled="true"
              >
                No pages match “{{ routeSearchQuery.trim() }}”.
              </li>
              <li
                v-for="(row, idx) in routeSearchResults"
                v-else
                :id="'route-search-opt-' + idx"
                :key="row.path"
                class="route-search-item"
                role="option"
                :class="{ 'is-active': idx === routeSearchSelectedIndex }"
                :aria-selected="idx === routeSearchSelectedIndex"
                @mousedown.prevent="goRouteSearchResult(row)"
                @mouseenter="routeSearchSelectedIndex = idx"
              >
                <div class="route-search-item-main">
                  <span class="route-search-title">{{ row.title }}</span>
                  <span v-if="row.subtitle" class="route-search-subtitle">{{ row.subtitle }}</span>
                </div>
                <span class="route-search-category">{{ row.category }}</span>
                <code class="route-search-path">{{ row.path }}</code>
              </li>
            </ul>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script>
import { useThemeStore } from "../../../themestores/theme";
import { useCartStore } from "../../../cartstore/cartStore";
import { useAuthStore } from "../../../authen/auth";
import { mapStores, mapState, mapActions } from "pinia";
import { buildApiUrl } from "@/config/api";
import {
  AUTH_REDIRECT_PATHS,
  navigateToSignIn,
} from "@/utils/authRedirect";
import { searchCustomerNavRoutes } from "@/utils/customerNavSearchRoutes";
import { PATHFINDER_ROUTE_NAME } from "@/routes/pathfinder/constants";
import { THE_ABLE_ROUTE_NAME } from "@/routes/theable/constants";
import { dispatchAppToast } from "@/utils/toast";
import logoLight from "../../../assets/ipalogo.png";
import logoDark from "../../../assets/ipalogo_dark.png";

export default {
  name: "MainNavbar",
  data() {
    return {
      menuOpen: false,
      lastScrollY: 0,
      hidden: false,
      userMenuOpen: false,
      showAccountSubmenu: false,
      teamInvitesNavLoading: false,
      routeSearchOpen: false,
      routeSearchQuery: "",
      routeSearchSelectedIndex: 0,
      routeSearchPrevBodyOverflow: "",
      pathfinderRouteName: PATHFINDER_ROUTE_NAME,
      theAbleRouteName: THE_ABLE_ROUTE_NAME,
    };
  },
  computed: {
    // 🌓 Theme store
    ...mapStores(useThemeStore),
    ...mapState(useThemeStore, ["theme"]),

    // 🛒 Cart store
    ...mapState(useCartStore, ["cartCount"]),

    // 🔐 Auth store
    ...mapStores(useAuthStore),
    ...mapState(useAuthStore, ["isAuthenticated"]),
    
    // Prefer mapState isAuthenticated; authStore is fallback for Pinia mapStores naming
    authStatus() {
      return !!(this.isAuthenticated ?? this.authStore?.isAuthenticated);
    },

    currentLogo() {
      return this.theme === "dark" ? logoDark : logoLight;
    },
    
    isMobile() {
      return window.innerWidth <= 768;
    },

    routeSearchResults() {
      return searchCustomerNavRoutes(this.routeSearchQuery, 80);
    },

    activeRouteSearchId() {
      if (!this.routeSearchResults.length) return undefined;
      const idx = Math.min(this.routeSearchSelectedIndex, this.routeSearchResults.length - 1);
      return "route-search-opt-" + idx;
    },
  },
  methods: {
    ...mapActions(useThemeStore, ["toggleTheme"]),

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      if (this.menuOpen) {
        this.closeUserMenu();
      }
    },

    closeMobileNav() {
      this.menuOpen = false;
    },

    /** Click-outside directive: only react when the user menu is open (avoids redundant work). */
    onUserMenuClickOutside() {
      if (this.userMenuOpen) {
        this.closeUserMenu();
      }
    },

    toggleUserMenu() {
      const next = !this.userMenuOpen;
      if (next && typeof window !== "undefined" && window.innerWidth <= 768) {
        this.menuOpen = false;
      }
      this.userMenuOpen = next;
      if (!this.userMenuOpen) {
        this.showAccountSubmenu = false;
      }
    },

    closeUserMenu() {
      this.userMenuOpen = false;
      this.showAccountSubmenu = false;
      // Do not set menuOpen here: toggleMenu() calls closeUserMenu() when opening the
      // mobile drawer, and clearing menuOpen would immediately close it again.
    },

    onGlobalDocumentClick(e) {
      if (!this.menuOpen) return;
      const nav = this.$el?.querySelector?.("nav#main-nav");
      const menuBtn = this.$el?.querySelector?.(".menu-btn");
      if (nav?.contains(e.target) || menuBtn?.contains(e.target)) {
        return;
      }
      this.menuOpen = false;
    },

    onEscapeKey(e) {
      if (e.key !== "Escape") return;
      if (this.routeSearchOpen) {
        this.closeRouteSearch();
        return;
      }
      this.closeMobileNav();
      this.closeUserMenu();
    },

    onRouteSearchHotkey(e) {
      if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        if (this.routeSearchOpen) {
          this.closeRouteSearch();
        } else {
          this.openRouteSearch();
        }
      }
    },

    openRouteSearch() {
      this.routeSearchOpen = true;
      this.routeSearchQuery = "";
      this.routeSearchSelectedIndex = 0;
      this.closeUserMenu();
      this.menuOpen = false;
      this.routeSearchPrevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      this.$nextTick(() => {
        this.$refs.routeSearchInput?.focus?.();
      });
    },

    closeRouteSearch() {
      if (!this.routeSearchOpen) return;
      this.routeSearchOpen = false;
      document.body.style.overflow = this.routeSearchPrevBodyOverflow || "";
      this.routeSearchPrevBodyOverflow = "";
    },

    moveRouteSearchSelection(delta) {
      const n = this.routeSearchResults.length;
      if (!n) return;
      this.routeSearchSelectedIndex = (this.routeSearchSelectedIndex + delta + n) % n;
      this.$nextTick(() => {
        const el = document.getElementById(this.activeRouteSearchId);
        el?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
      });
    },

    confirmRouteSearchSelection() {
      const row = this.routeSearchResults[this.routeSearchSelectedIndex];
      if (row) this.goRouteSearchResult(row);
    },

    goRouteSearchResult(row) {
      if (!row?.path) return;
      this.closeRouteSearch();
      this.$router.push(row.path).catch(() => {});
    },
    
    handleLogout() {
      this.authStore.logout();
      this.closeUserMenu();
      this.$router.push("/");
    },
    
    handleScroll() {
      const currentScrollY = window.scrollY;
      this.hidden = currentScrollY > this.lastScrollY && currentScrollY > 80;
      this.lastScrollY = currentScrollY;
    },
    
    handleResize() {
      // Force re-render on resize to update mobile detection
      this.$forceUpdate();
    },

    // 🛒 Navigate to cart page
    goToCart() {
      this.$router.push("/ipromiseafrica/cart");
    },
    
    // ⚽ Handle "My Pitch" click - check ownership first
    async handleMyPitchClick() {
      this.closeUserMenu();
      
      if (!this.authStatus) {
        this.showToast("Login Required", "Please log in to access your pitch", "error");
        navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.FACILITY_OWNER_REGISTER);
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        if (!token || !token.trim()) {
          this.showToast("Authentication Required", "Please log in to access your pitch", "error");
          navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.FACILITY_OWNER_REGISTER);
          return;
        }

        const response = await fetch(buildApiUrl("/api/facilities/check-ownership"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.trim()}`
          }
        });

        if (!response.ok) {
          let msg = "Failed to check pitch ownership";
          try {
            const errBody = await response.json();
            if (errBody.detail) msg = typeof errBody.detail === "string" ? errBody.detail : "Failed to check pitch ownership";
          } catch (_) { /* ignore parse error, use default msg */ }
          throw new Error(msg);
        }

        let data = {};
        try {
          data = await response.json();
        } catch (_) {
          throw new Error("Invalid response from server");
        }

        if (data.has_facility ?? data.has_pitch) {
          // User has a pitch, navigate to my pitch page
          this.$router.push("/ipromiseafrica/facility/my-facility");
        } else {
          // No pitch registered, show toast and route to registration
          this.showToast(
            "No Pitch Registered",
            "You don't have a pitch registered yet. Please register one to continue.",
            "info"
          );
          // Route to pitch registration after a short delay
          setTimeout(() => {
            this.$router.push("/ipromiseafrica/facility/owner-registration");
          }, 2000);
        }
      } catch (error) {
        console.error("Error checking pitch ownership:", error);
        this.showToast(
          "Error",
          "Failed to check pitch ownership. Please try again.",
          "error"
        );
      }
    },
    
    // 🏪 Handle "Seller Dashboard" click - check seller account first
    async handleSellerDashboardClick() {
      this.closeUserMenu();
      
      if (!this.authStatus) {
        this.showToast("Login Required", "Please log in to access your seller dashboard", "error");
        navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.SELLER_SIGNUP);
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        if (!token || !token.trim()) {
          this.showToast("Authentication Required", "Please log in to access your seller dashboard", "error");
          navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.SELLER_SIGNUP);
          return;
        }

        const response = await fetch(buildApiUrl("/seller/check-account"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.trim()}`
          }
        });

        if (!response.ok) {
          let msg = "Failed to check seller account";
          try {
            const errBody = await response.json();
            if (errBody.detail) msg = typeof errBody.detail === "string" ? errBody.detail : "Failed to check seller account";
          } catch (_) { /* ignore parse error, use default msg */ }
          throw new Error(msg);
        }

        let data = {};
        try {
          data = await response.json();
        } catch (_) {
          throw new Error("Invalid response from server");
        }

        if (data.has_seller_account) {
          // User has a seller account, navigate to seller dashboard
          this.$router.push("/ipromiseafrica/resale/seller-dashboard");
        } else {
          // No seller account registered, show toast and route to registration
          this.showToast(
            "No Seller Account",
            "You don't have a seller account registered yet. Please register one to continue.",
            "info"
          );
          // Route to seller registration after a short delay
          setTimeout(() => {
            this.$router.push("/ipromiseafrica/resale/seller-sign-up");
          }, 2000);
        }
      } catch (error) {
        console.error("Error checking seller account:", error);
        this.showToast(
          "Error",
          "Failed to check seller account. Please try again.",
          "error"
        );
      }
    },

    // ✉️ Team invites — auth + token checks, then prefetch `/api/teams/invites/received` before routing
    async handleTeamInvitesClick() {
      this.closeUserMenu();

      if (!this.authStatus) {
        this.showToast("Login required", "Please log in to view your team invites.", "error");
        return;
      }

      if (this.teamInvitesNavLoading) return;

      const token = (localStorage.getItem("access_token") || "").trim();
      if (!token) {
        this.showToast("Authentication required", "Please sign in again to view team invites.", "error");
        return;
      }

      this.teamInvitesNavLoading = true;
      try {
        const response = await fetch(buildApiUrl("/api/teams/invites/received"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 401) {
          this.showToast("Session expired", "Please sign in again to view team invites.", "error");
          this.$router
            .push({
              name: "CustomerSignIn",
              query: { redirect: "/ipromiseafrica/matchup/invites" },
            })
            .catch(() => {});
          return;
        }

        if (!response.ok) {
          let msg = "Could not load team invites.";
          try {
            const errBody = await response.json();
            if (errBody.detail) {
              msg = typeof errBody.detail === "string" ? errBody.detail : msg;
            }
          } catch (_) {
            /* use default */
          }
          throw new Error(msg);
        }

        let data = {};
        try {
          data = await response.json();
        } catch (_) {
          throw new Error("Invalid response from server.");
        }

        if (!data || typeof data !== "object" || !Array.isArray(data.invites)) {
          throw new Error("Unexpected response when loading invites.");
        }

        await this.$router.push({ name: "ViewInvites" }).catch(() => {});
      } catch (error) {
        console.error("Error loading team invites:", error);
        this.showToast(
          "Team invites",
          error.message || "Failed to load team invites. Please try again.",
          "error"
        );
      } finally {
        this.teamInvitesNavLoading = false;
      }
    },

    // 👥 Handle "My Team" click - check team ownership first
    async handleMyTeamClick() {
      this.closeUserMenu();
      
      if (!this.authStatus) {
        this.showToast("Login Required", "Please log in to access your team", "error");
        navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.FOOTBALL_TEAM_REGISTER);
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        if (!token || !token.trim()) {
          this.showToast("Authentication Required", "Please log in to access your team", "error");
          navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.FOOTBALL_TEAM_REGISTER);
          return;
        }

        const response = await fetch(buildApiUrl("/api/teams/check-team"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.trim()}`
          }
        });

        if (!response.ok) {
          let msg = "Failed to check team";
          try {
            const errBody = await response.json();
            if (errBody.detail) msg = typeof errBody.detail === "string" ? errBody.detail : "Failed to check team";
          } catch (_) { /* ignore parse error, use default msg */ }
          throw new Error(msg);
        }

        let data = {};
        try {
          data = await response.json();
        } catch (_) {
          throw new Error("Invalid response from server");
        }

        if (data.has_team) {
          // User has a team, navigate to team dashboard
          this.$router.push("/ipromiseafrica/matchup/football/dashboard");
        } else {
          // No team registered, show toast and route to registration
          this.showToast(
            "No Team Registered",
            "You don't have a team registered yet. Please create one to continue.",
            "info"
          );
          // Route to team registration after a short delay
          setTimeout(() => {
            this.$router.push("/ipromiseafrica/matchup/register");
          }, 2000);
        }
      } catch (error) {
        console.error("Error checking team:", error);
        this.showToast(
          "Error",
          "Failed to check team. Please try again.",
          "error"
        );
      }
    },
    
    // 👤 Handle "Player Dashboard" click - check team membership first
    async handlePlayerDashboardClick() {
      this.closeUserMenu();
      
      if (!this.authStatus) {
        this.showToast("Login Required", "Please log in to access your player dashboard", "error");
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        if (!token || !token.trim()) {
          this.showToast("Authentication Required", "Please log in to access your player dashboard", "error");
          return;
        }

        const response = await fetch(buildApiUrl("/api/teams/check-team-member"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.trim()}`
          }
        });

        if (!response.ok) {
          let msg = "Failed to check team membership";
          try {
            const errBody = await response.json();
            if (errBody.detail) msg = typeof errBody.detail === "string" ? errBody.detail : "Failed to check team membership";
          } catch (_) { /* ignore parse error, use default msg */ }
          throw new Error(msg);
        }

        let data = {};
        try {
          data = await response.json();
        } catch (_) {
          throw new Error("Invalid response from server");
        }

        if (data.is_team_member) {
          // User is a team member, navigate to player dashboard
          this.$router.push("/ipromiseafrica/matchup/player/dashboard");
        } else {
          // Not a team member, show toast and do NOT route
          this.showToast(
            "Team Membership Required",
            "You currently don't belong to any team, you require a team invite.",
            "info"
          );
          // No routing occurs - user stays on current page
        }
      } catch (error) {
        console.error("Error checking team membership:", error);
        this.showToast(
          "Error",
          "Failed to check team membership. Please try again.",
          "error"
        );
      }
    },
    
    showToast(title, message, type = "info") {
      dispatchAppToast(title, message, type);
    },
  },
  watch: {
    $route() {
      this.closeMobileNav();
      this.closeUserMenu();
      this.closeRouteSearch();
    },
    routeSearchQuery() {
      this.routeSearchSelectedIndex = 0;
    },
    routeSearchResults(list) {
      if (this.routeSearchSelectedIndex >= list.length) {
        this.routeSearchSelectedIndex = Math.max(0, list.length - 1);
      }
    },
  },

  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("click", this.onGlobalDocumentClick);
    document.addEventListener("keydown", this.onEscapeKey);
    document.addEventListener("keydown", this.onRouteSearchHotkey);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("click", this.onGlobalDocumentClick);
    document.removeEventListener("keydown", this.onEscapeKey);
    document.removeEventListener("keydown", this.onRouteSearchHotkey);
    document.body.style.overflow = this.routeSearchPrevBodyOverflow || "";
  },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  }
};
</script>

<style scoped>
/* === Base Navbar Styling === */
section {
  width: 100%;
  font-family: "Poppins", sans-serif;
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: clamp(60px, 8vh, 70px);
  padding: 0 clamp(12px, 2vw, 24px);
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background-color: var(--background-color2);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease-in-out;
  /* Allow absolutely positioned <nav> drawer to paint below the bar (overflow-x:hidden clips Y in many browsers) */
  overflow: visible;
}

.navbar.hidden {
  transform: translateY(-100%);
}

/* === Logo === */
.logo {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  line-height: 0;
}

.logo img {
  height: clamp(36px, 5vw, 42px);
  max-width: 100%;
  cursor: pointer;
  transition: transform 0.3s ease;
  object-fit: contain;
}

.logo img:hover {
  transform: scale(1.05);
}

/* === Navigation Items === */
nav {
  display: flex;
  align-items: center;
  gap: clamp(12px, 2vw, 28px);
  flex-wrap: nowrap;
  flex: 1;
  justify-content: center;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

nav::-webkit-scrollbar {
  display: none;
}

.nav-item {
  font-size: clamp(0.7rem, 1vw, 0.875rem);
  font-weight: 500;
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  padding: 6px 0;
  position: relative;
  text-transform: capitalize;
  letter-spacing: 0.3px;
}

/* RESET router-link default styles */
a.nav-item {
  all: unset;
  display: block;
}

/* Reapply nav-item styling for all elements */
a.nav-item,
span.nav-item,
p.nav-item {
  font-size: clamp(0.7rem, 1vw, 0.875rem);
  font-weight: 500;
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  padding: 6px 0;
  position: relative;
  text-transform: capitalize;
  letter-spacing: 0.3px;
}

/* Hover effect for clickable items */
a.nav-item:hover,
span.nav-item:not(.nav-item-disabled):hover {
  color: var(--border-color-hover);
  transform: translateY(-2px);
}

/* Underline effect on hover for links */
a.nav-item::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--border-color-hover);
  transition: width 0.3s ease;
}

a.nav-item:hover::after {
  width: 100%;
}

/* Disabled state for pending items */
.nav-item-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  position: relative;
}

.nav-item-disabled::before {
  content: 'Coming Soon';
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.6rem;
  color: var(--border-color-hover);
  opacity: 0;
  transition: opacity 0.3s ease;
  white-space: nowrap;
  pointer-events: none;
}

.nav-item-disabled:hover::before {
  opacity: 1;
}

/* === Icons === */
.icons {
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.5vw, 18px);
  flex-shrink: 0;
}

.icons i {
  color: var(--text-color1);
  font-size: clamp(0.9rem, 1.2vw, 1.05rem);
  cursor: pointer;
  transition: color 0.3s ease, transform 0.3s ease;
}
.icons i:hover {
  color: var(--border-color-hover);
  transform: scale(1.15);
}

.search-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: var(--text-color1);
  font-size: clamp(0.9rem, 1.2vw, 1.05rem);
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.3s ease, transform 0.3s ease, background 0.2s ease;
}

.search-trigger:hover {
  color: var(--border-color-hover);
  transform: scale(1.12);
}

.search-trigger:focus-visible {
  outline: 2px solid var(--border-color-hover3);
  outline-offset: 2px;
}

/* —— Route search (command palette) —— */
.route-search-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: clamp(72px, 12vh, 120px) clamp(16px, 4vw, 32px) 24px;
  background: color-mix(in srgb, var(--background-color2) 55%, transparent);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.route-search-panel {
  width: min(560px, 100%);
  max-height: min(72vh, 640px);
  display: flex;
  flex-direction: column;
  background: var(--background-color2);
  color: var(--text-color1);
  border-radius: 16px;
  border: 1px solid var(--border-color-hover3);
  box-shadow: 0 24px 48px var(--box-shadow), 0 0 0 1px color-mix(in srgb, var(--border-color-hover) 20%, transparent);
  overflow: hidden;
}

.route-search-field {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid color-mix(in srgb, var(--border-color-hover3) 40%, transparent);
  background: var(--background-color1);
}

.route-search-field-icon {
  color: var(--border-color-hover);
  font-size: 1.05rem;
  flex-shrink: 0;
}

.route-search-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--text-color1);
  font-size: 1rem;
  font-family: inherit;
  outline: none;
}

.route-search-input::placeholder {
  color: var(--text-color2);
}

.route-search-close {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--text-color2);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.route-search-close:hover {
  background: var(--background-color3);
  color: var(--text-color1);
}

.route-search-meta {
  margin: 0;
  padding: 8px 16px 10px;
  font-size: 0.72rem;
  color: var(--text-color2);
  line-height: 1.5;
  border-bottom: 1px solid color-mix(in srgb, var(--border-color-hover3) 35%, transparent);
}

.route-search-kbd {
  display: inline-block;
  padding: 1px 5px;
  margin: 0 2px;
  font-size: 0.65rem;
  font-family: ui-monospace, monospace;
  border-radius: 4px;
  border: 1px solid var(--border-color-hover3);
  background: var(--background-color3);
  color: var(--text-color1);
}

.route-search-list {
  list-style: none;
  margin: 0;
  padding: 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 120px;
}

.route-search-empty {
  padding: 28px 16px;
  text-align: center;
  color: var(--text-color2);
  font-size: 0.9rem;
}

.route-search-item {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 2px 12px;
  align-items: start;
  padding: 10px 12px;
  margin-bottom: 4px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  border: 1px solid transparent;
}

.route-search-item:hover,
.route-search-item.is-active {
  background: var(--background-color3);
  border-color: color-mix(in srgb, var(--border-color-hover) 35%, transparent);
}

.route-search-item.is-active {
  box-shadow: 0 0 0 1px var(--border-color-hover3);
}

.route-search-item-main {
  grid-column: 1;
  grid-row: 1 / span 2;
  min-width: 0;
}

.route-search-title {
  display: block;
  font-weight: 600;
  font-size: 0.92rem;
  color: var(--text-color1);
}

.route-search-subtitle {
  display: block;
  font-size: 0.78rem;
  color: var(--text-color2);
  margin-top: 2px;
  line-height: 1.35;
}

.route-search-category {
  grid-column: 2;
  grid-row: 1;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--border-color-hover);
  white-space: nowrap;
  padding-top: 2px;
}

.route-search-path {
  grid-column: 2;
  grid-row: 2;
  font-size: 0.65rem;
  color: var(--text-color2);
  font-family: ui-monospace, "Cascadia Code", monospace;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  justify-self: end;
  background: color-mix(in srgb, var(--background-color1) 80%, transparent);
  padding: 2px 6px;
  border-radius: 4px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.route-search-overlay-enter-active,
.route-search-overlay-leave-active {
  transition: opacity 0.2s ease;
}

.route-search-overlay-enter-active .route-search-panel,
.route-search-overlay-leave-active .route-search-panel {
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.route-search-overlay-enter-from,
.route-search-overlay-leave-to {
  opacity: 0;
}

.route-search-overlay-enter-from .route-search-panel,
.route-search-overlay-leave-to .route-search-panel {
  opacity: 0;
  transform: translateY(-12px) scale(0.98);
}

@media (max-width: 480px) {
  .route-search-item {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  .route-search-category,
  .route-search-path {
    grid-column: 1;
    grid-row: auto;
    justify-self: start;
    max-width: 100%;
  }
}

/* === User Menu Dropdown === */
.user-menu-wrapper {
  position: relative;
}

.user-menu-trigger {
  display: flex;
  align-items: center;
  gap: clamp(4px, 0.5vw, 6px);
  background: transparent;
  border: none;
  color: var(--text-color1);
  font-size: clamp(1rem, 1.5vw, 1.2rem);
  cursor: pointer;
  padding: clamp(4px, 0.8vw, 6px) clamp(8px, 1.2vw, 10px);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.user-menu-trigger:hover {
  background: var(--background-color1);
  color: var(--border-color-hover);
}

.dropdown-arrow {
  font-size: 0.7rem;
  transition: transform 0.3s ease;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.user-menu-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: clamp(200px, 25vw, 220px);
  max-width: 90vw;
  background: var(--background-color2);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: clamp(6px, 1vw, 8px);
  z-index: 1000;
  border: 1px solid var(--border-color-hover);
  overflow: visible;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.5vw, 12px);
  padding: clamp(10px, 1.5vw, 12px) clamp(14px, 2vw, 16px);
  color: var(--text-color1);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  font-size: clamp(0.85rem, 1.2vw, 0.9rem);
  font-weight: 500;
  white-space: nowrap;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
}

.menu-item:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

.menu-item:disabled:hover {
  background: transparent;
  color: var(--text-color1);
}

/* router-link renders as <a>; keep it clickable and full-width like buttons */
a.menu-item.menu-item-link {
  display: flex;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

.menu-item:hover {
  background: var(--background-color1);
  color: var(--border-color-hover);
  transform: translateX(4px);
}

.menu-item i {
  font-size: clamp(0.9rem, 1.2vw, 1rem);
  width: clamp(18px, 2.5vw, 20px);
  text-align: center;
  color: var(--border-color-hover);
  flex-shrink: 0;
}

.menu-item.has-submenu {
  position: relative;
}

.menu-item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.submenu-arrow {
  margin-right: auto;
  margin-left: 0;
  font-size: 0.7rem;
  transition: transform 0.3s ease;
}

.submenu-arrow.rotated {
  transform: rotate(-90deg);
}

.menu-item.has-submenu:hover .submenu-arrow {
  transform: translateX(-4px);
}

.menu-item.has-submenu:hover .submenu-arrow.rotated {
  transform: rotate(-90deg) translateX(-4px);
}

.submenu {
  position: absolute;
  right: calc(100% + 8px);
  top: 0;
  min-width: clamp(160px, 20vw, 180px);
  max-width: 85vw;
  background: var(--background-color2);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: clamp(6px, 1vw, 8px);
  border: 1px solid var(--border-color-hover);
  z-index: 1002;
  white-space: nowrap;
}

/* Desktop: submenu appears on the left */
@media (min-width: 769px) {
  .submenu {
    right: calc(100% + 8px);
    left: auto;
  }
}


.submenu-item {
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.5vw, 12px);
  padding: clamp(8px, 1.2vw, 10px) clamp(12px, 1.8vw, 14px);
  color: var(--text-color1);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  font-size: clamp(0.8rem, 1.1vw, 0.85rem);
  font-weight: 500;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  white-space: nowrap;
}

.submenu-item:hover {
  background: var(--background-color1);
  color: var(--border-color-hover);
}

.submenu-item.logout-btn {
  color: #ef4444;
}

.submenu-item.logout-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Top-level logout (e.g. mobile flat account row) */
.menu-item.logout-btn {
  color: #ef4444;
}

.menu-item.logout-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.submenu-item i {
  font-size: clamp(0.85rem, 1.1vw, 0.9rem);
  width: clamp(16px, 2.2vw, 18px);
  text-align: center;
  flex-shrink: 0;
}

/* Dropdown Transitions */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.submenu-fade-enter-active,
.submenu-fade-leave-active {
  transition: all 0.2s ease;
}

.submenu-fade-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.submenu-fade-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

/* === Cart Icon === */
.cart-icon {
  position: relative;
  cursor: pointer;
}
.cart-badge {
  position: absolute;
  top: clamp(-6px, -0.8vw, -5px);
  right: clamp(-10px, -1vw, -8px);
  background: var(--border-color-hover);
  color: var(--text-color3);
  border-radius: 50%;
  padding: clamp(1px, 0.3vw, 2px) clamp(4px, 0.8vw, 6px);
  font-size: clamp(0.6rem, 0.9vw, 0.65rem);
  font-weight: bold;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* === Theme Toggle === */
#theme-toggle {
  background: transparent;
  border: none;
  color: var(--text-color1);
  font-size: clamp(0.95rem, 1.3vw, 1.1rem);
  cursor: pointer;
  transition: color 0.3s ease;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
#theme-toggle:hover {
  color: var(--border-color-hover);
}

/* === Mobile menu toggle (≤768px) === */
.menu-btn {
  display: none;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 6px 8px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-color1);
  cursor: pointer;
  font: inherit;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
}

.menu-btn i {
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  pointer-events: none;
}

.menu-btn:hover,
.menu-btn:focus-visible {
  color: var(--border-color-hover);
  outline: none;
  box-shadow: 0 0 0 2px var(--border-color-hover, rgba(0, 255, 255, 0.4));
}

/* === Tablet and Below (1024px) === */
@media (max-width: 1024px) {
  nav {
    gap: clamp(10px, 1.5vw, 20px);
  }
  
  .nav-item {
    font-size: clamp(0.7rem, 1.2vw, 0.8rem);
  }
  
  .icons {
    gap: clamp(8px, 1.2vw, 14px);
  }
}

/* === Tablet Portrait and Below (768px) === */
@media (max-width: 768px) {
  .navbar {
    padding: 0 clamp(10px, 2vw, 16px);
  }
  
  nav#main-nav {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    background: var(--background-color2);
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: clamp(15px, 3vw, 20px) 0;
    border-top: 1px solid var(--border-color-hover);
    transform: translateY(-120%);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    max-height: calc(100vh - 70px);
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 1001;
    transition:
      transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
      opacity 0.25s ease,
      visibility 0.25s ease;
  }

  nav#main-nav.open {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    /* Above .user-menu-wrapper (1002) / .user-menu-dropdown (1003) so the drawer is never covered */
    z-index: 1100;
  }

  .nav-item {
    font-size: clamp(0.8rem, 2vw, 0.875rem);
    padding: clamp(8px, 2vw, 10px) 0;
    width: 100%;
    text-align: center;
  }

  a.nav-item::after {
    display: none;
  }

  .nav-item-disabled::before {
    position: static;
    transform: none;
    display: block;
    margin-top: 4px;
    font-size: clamp(0.6rem, 1.5vw, 0.65rem);
  }

  .menu-btn {
    display: flex;
  }

  .user-menu-dropdown {
    position: fixed;
    top: auto;
    bottom: 20px;
    right: 10px;
    left: auto;
    max-width: calc(100vw - 20px);
    min-width: auto;
    width: calc(100vw - 40px);
    max-height: calc(100vh - 100px);
    overflow-y: auto;
  }

  .submenu {
    position: static;
    left: auto;
    right: auto;
    top: auto;
    margin-top: 8px;
    margin-left: 20px;
    width: calc(100% - 20px);
    max-width: none;
    min-width: auto;
    box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.1);
    border-left: 3px solid var(--border-color-hover);
    border-radius: 8px;
  }
  
  .menu-item.has-submenu {
    position: relative;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .menu-item-header {
    width: 100%;
  }
  
  .submenu-arrow {
    transform: rotate(90deg);
  }
  
  .menu-item.has-submenu:hover .submenu-arrow,
  .menu-item.has-submenu .submenu-arrow.rotated {
    transform: rotate(90deg) translateX(4px);
  }
  
  .menu-item {
    font-size: clamp(0.85rem, 2vw, 0.9rem);
    padding: clamp(10px, 2vw, 12px) clamp(12px, 2vw, 16px);
  }
  
  .submenu-item {
    font-size: clamp(0.8rem, 1.8vw, 0.85rem);
    padding: clamp(8px, 1.5vw, 10px) clamp(12px, 2vw, 14px);
  }
}

/* === Mobile Landscape and Below (640px) === */
@media (max-width: 640px) {
  .navbar {
    height: clamp(55px, 7vh, 65px);
  }
  
  nav {
    top: 100%;
    padding: clamp(12px, 2.5vw, 18px) 0;
  }
  
  .user-menu-dropdown {
    position: fixed !important;
    bottom: 15px !important;
    right: 10px !important;
    width: calc(100vw - 20px) !important;
    max-width: calc(100vw - 20px) !important;
  }
  
  .submenu {
    position: static !important;
    width: calc(100% - 16px) !important;
    margin-left: 16px;
    right: auto !important;
    left: auto !important;
  }
}

/* === Small Mobile (480px) === */
@media (max-width: 480px) {
  .navbar {
    padding: 0 clamp(8px, 2vw, 12px);
    height: clamp(50px, 6vh, 60px);
  }
  
  .logo img {
    height: clamp(32px, 6vw, 38px);
  }
  
  nav {
    top: 100%;
    gap: 6px;
    padding: clamp(10px, 2vw, 15px) 0;
  }

  .nav-item {
    font-size: clamp(0.75rem, 2.5vw, 0.85rem);
    padding: clamp(6px, 1.5vw, 8px) 0;
  }

  .icons {
    gap: clamp(6px, 1.5vw, 10px);
  }
  
  .icons i {
    font-size: clamp(0.85rem, 2vw, 0.95rem);
  }

  .user-menu-trigger {
    font-size: clamp(0.9rem, 2vw, 1rem);
    padding: clamp(3px, 0.6vw, 5px) clamp(6px, 1vw, 8px);
  }

  .user-menu-dropdown {
    bottom: 10px !important;
    right: 8px !important;
    width: calc(100vw - 16px) !important;
    padding: clamp(4px, 1vw, 6px);
  }
  
  .submenu {
    width: calc(100% - 12px) !important;
    margin-left: 12px;
    margin-top: 6px;
  }
  
  .menu-item {
    padding: clamp(8px, 1.8vw, 10px) clamp(10px, 2vw, 12px);
    font-size: clamp(0.8rem, 2.2vw, 0.85rem);
    white-space: normal;
  }

  .submenu-item {
    padding: clamp(6px, 1.2vw, 8px) clamp(10px, 1.8vw, 12px);
    font-size: clamp(0.75rem, 2vw, 0.8rem);
  }
  
  .cart-badge {
    font-size: clamp(0.6rem, 1.5vw, 0.65rem);
    padding: clamp(1px, 0.3vw, 2px) clamp(4px, 1vw, 6px);
  }
}

/* === Extra Small Mobile (360px) === */
@media (max-width: 360px) {
  .navbar {
    padding: 0 8px;
    height: 50px;
  }
  
  .logo img {
    height: 30px;
  }
  
  nav {
    padding: 12px 0;
  }
  
  .nav-item {
    font-size: 0.75rem;
    padding: 6px 0;
  }
  
  .icons {
    gap: 6px;
  }
  
  .icons i {
    font-size: 0.85rem;
  }
  
  .user-menu-dropdown {
    bottom: 8px !important;
    right: 6px !important;
    width: calc(100vw - 12px) !important;
    padding: 4px;
  }
  
  .submenu {
    position: static !important;
    width: calc(100% - 8px) !important;
    margin-left: 8px;
    margin-top: 4px;
    right: auto !important;
    left: auto !important;
  }
  
  .menu-item {
    padding: 8px 10px;
    font-size: 0.8rem;
    white-space: normal;
  }
  
  .cart-badge {
    min-width: 16px;
    height: 16px;
    font-size: 0.6rem;
    top: -4px;
    right: -6px;
  }
}

/* === Touch-friendly improvements === */
@media (hover: none) and (pointer: coarse) {
  .nav-item,
  .menu-item,
  .submenu-item,
  .user-menu-trigger,
  .cart-icon,
  #theme-toggle,
  .menu-btn {
    min-height: 44px;
    min-width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .menu-item,
  .submenu-item {
    padding: 12px 16px;
  }
}

/* === Mobile user dropdown polish (≤768px; complements rules above) === */
@media (max-width: 768px) {
  .user-menu-wrapper {
    position: relative;
    z-index: 1002;
  }

  .user-menu-dropdown,
  .user-menu-dropdown.mobile-dropdown {
    position: fixed !important;
    top: auto !important;
    bottom: 15px !important;
    right: 10px !important;
    left: auto !important;
    max-width: calc(100vw - 20px) !important;
    width: calc(100vw - 20px) !important;
    min-width: auto !important;
    max-height: calc(100vh - 80px);
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    transform-origin: bottom right;
    scrollbar-width: thin;
    scrollbar-color: var(--border-color-hover) transparent;
    z-index: 1003;
  }

  .user-menu-dropdown::-webkit-scrollbar,
  .user-menu-dropdown.mobile-dropdown::-webkit-scrollbar {
    width: 4px;
  }

  .user-menu-dropdown::-webkit-scrollbar-track,
  .user-menu-dropdown.mobile-dropdown::-webkit-scrollbar-track {
    background: transparent;
  }

  .user-menu-dropdown::-webkit-scrollbar-thumb,
  .user-menu-dropdown.mobile-dropdown::-webkit-scrollbar-thumb {
    background: var(--border-color-hover);
    border-radius: 2px;
  }

  .menu-item.has-submenu {
    flex-direction: column;
    align-items: flex-start;
  }

  .menu-item-header {
    width: 100%;
  }

  .submenu-arrow {
    transform: rotate(90deg);
  }

  .menu-item.has-submenu:hover .submenu-arrow,
  .menu-item.has-submenu .submenu-arrow.rotated {
    transform: rotate(90deg) translateX(4px);
  }

  .menu-item {
    white-space: normal;
    word-wrap: break-word;
  }

  .submenu-item {
    white-space: normal;
    word-wrap: break-word;
  }
}

/* === Landscape Orientation Adjustments === */
@media (max-height: 500px) and (orientation: landscape) {
  .navbar {
    height: 50px;
  }
  
  nav {
    max-height: calc(100vh - 50px);
    padding: 10px 0;
  }
  
  .nav-item {
    padding: 6px 0;
  }
}
</style>
